import type { ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Spin, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { numberToBRL } from 'v2/helpers/money';
import type { IFinancialInvoice } from 'v2/interfaces/financial';
import type { IUserData } from 'v2/interfaces/user';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementBlockedService } from 'v2/services/financialStatement/blocked';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import { Icon } from '@eduzz/ui-antd-theme';

import PaginationLoadMore from './PaginationLoadMore';

interface IProps {
  transactionDate: string;
  showBlockedDetails?: boolean;
}

const DESKTOP_COLUMN_WIDTHS = { icon: 32, invoice: 110, amount: 100, maxRows: 1 };
const MOBILE_COLUMN_WIDTHS = { icon: 28, invoice: 72, amount: 110, maxRows: 2 };

interface IInvoiceDetailsProps {
  invoiceId: number;
  className?: string;
  children: ReactNode;
  idPrefix: string;
  user: IUserData;
}

function InvoiceDetailsLink({ invoiceId, className, idPrefix, children }: IInvoiceDetailsProps) {
  return (
    <Link
      to={`/producer/invoice/details/${invoiceId}`}
      id={`${idPrefix}-${invoiceId}`}
      className={className ? className : ''}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </Link>
  );
}

function ContentInnerTable({ transactionDate, showBlockedDetails }: IProps) {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 599.95px)').matches);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [filters] = useObservable(() => financialStatementService.getFilters(), []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 599.95px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.loadDetails(transactionDate)
        : financialStatementService.loadDetails(transactionDate),
    [
      transactionDate,
      showBlockedDetails,
      filters?.movement_type,
      filters?.transaction_type,
      filters?.search_type,
      filters?.search_text
    ]
  );

  const [currEntries, setCurrEntries] = useState<IFinancialInvoice[] | null>(null);
  const [currPage, setCurrPage] = useState(1);

  const [isLoading] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.isLoadingDetails()
        : financialStatementService.isLoadingDetails(),
    [showBlockedDetails]
  );
  const [pagination] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.getPaginationResponseDetails()
        : financialStatementService.getPaginationResponseDetails(),
    [showBlockedDetails]
  );
  const [entries] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.getFinancialStatementBlockedDetails()
        : financialStatementService.getFinancialStatementDetails(),
    [showBlockedDetails]
  );

  useEffect(() => {
    if (!entries) {
      setCurrEntries(null);
      setCurrPage(1);
      return;
    }

    const nextEntries = entries as unknown as IFinancialInvoice[];

    if (!pagination?.current_page || pagination.current_page <= 1) {
      setCurrEntries([...nextEntries]);
      setCurrPage(1);
      return;
    }

    if (pagination.current_page > currPage) {
      setCurrEntries(prev => [...(prev || []), ...nextEntries]);
      setCurrPage(pagination.current_page);
    }
  }, [entries, pagination, currPage]);

  const columnWidths = isMobile ? MOBILE_COLUMN_WIDTHS : DESKTOP_COLUMN_WIDTHS;

  const columns: ColumnsType<IFinancialInvoice> = [
    {
      key: 'icon',
      width: columnWidths.icon,
      render: (_, entry) => {
        const isPositive = entry.amount > 0;
        return isPositive && !showBlockedDetails ? (
          <Icon.ArrowDown size={16} className='text-(--uizz-antd-color-success)' />
        ) : (
          <Icon.ArrowUp size={16} className='text-(--uizz-antd-color-error)' />
        );
      }
    },
    {
      title: 'Fatura',
      key: 'invoice',
      width: columnWidths.invoice,
      render: (_, entry) =>
        entry.invoice_id ? (
          <InvoiceDetailsLink
            user={user}
            invoiceId={entry.invoice_id}
            idPrefix='invoice'
            className='text-(--uizz-antd-color-primary)'
          >
            {entry.invoice_id}
          </InvoiceDetailsLink>
        ) : (
          '-'
        )
    },
    {
      title: 'Histórico',
      key: 'description',
      render: (_, entry) => (
        <div className='flex min-w-0 items-center gap-1'>
          <Typography.Paragraph
            ellipsis={{ rows: columnWidths.maxRows, tooltip: true }}
            className='mb-0 min-w-0 wrap-break-word'
          >
            {showBlockedDetails && entry.is_chargeback ? `[CHARGEBACK] - ${entry.description}` : entry.description}
          </Typography.Paragraph>
          {entry.is_installment_settlement && (
            <Tooltip
              placement='top'
              title='Parte do valor da Liquidação do Parcelamento é repassado de volta para você'
            >
              <Icon.Info size={14} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Valor',
      key: 'amount',
      width: columnWidths.amount,
      align: 'right',
      render: (_, entry) => {
        const isPositive = entry.amount > 0;
        const amountSign = isPositive && !showBlockedDetails ? '+' : '-';
        return (
          <span
            className={
              isPositive && !showBlockedDetails ? 'text-(--uizz-antd-color-success)' : 'text-(--uizz-antd-color-error)'
            }
          >
            {`${amountSign} `}
            <Mask>{`${numberToBRL(isPositive ? entry.amount : entry.amount * -1)}`}</Mask>
          </span>
        );
      }
    }
  ];

  if (!currEntries) {
    return (
      <div className='flex items-center justify-center p-5'>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Table
        size='small'
        tableLayout='fixed'
        columns={columns}
        dataSource={currEntries}
        rowKey={(record: IFinancialInvoice, index: number) => `Row-${record.invoice_id}-${index}`}
        pagination={false}
        loading={isLoading}
        className='[&_td]:text-sm [&_td]:text-(--uizz-antd-color-text-secondary) [&_td]:uppercase [&_th]:text-sm [&_th]:font-semibold [&_th]:text-(--uizz-antd-color-text)'
      />

      <PaginationLoadMore isLoading={isLoading} pagination={pagination} showBlockedDetails={showBlockedDetails} />
    </div>
  );
}

export default memo(ContentInnerTable);
